import { AxiosError } from "axios";
import * as Sentry from "@sentry/react";
import { hosts } from "@songtradr-sso/shared/common";
import { errorStaticText as text } from "../statics/error-text";

/** NormalizedException
 *
 * A custom error object to standardize handling of errors on the frontend.
 *
 * - If constructed with an already normalized error, returns it as is.
 * - If constructed with a generic Error, extracts error code from its message or name.
 * - If constructed with an AxiosError, extracts error code & status.
 * - If constructed with a string, uses it as the error code.
 * - The error message is generated using error code & static text.
 * - Defaults to generic code & message if specifics are unavailable.
 *
 * @param err - Any input to normalize, ideally an error
 * @param log - Whether to log the error to the console (default: true in non-production)
 */
export class NormalizedException implements Error {
  readonly name = "NormalizedException";
  readonly code: string = "Unexpected";
  readonly message: string = text.default;
  readonly status?: number;

  constructor(err?: unknown, log = hosts.environment !== "production") {
    if (err instanceof NormalizedException) return err;

    if (err instanceof Error) {
      this.code = text[err.message] ? err.message : this.code;
      this.message = err.message || this.message;
    }

    if (err instanceof AxiosError) {
      this.code = err.response?.data?.code || this.code;
      this.status = err.response?.status || 500;
    }

    if (typeof err === "string") {
      this.code = text[err] ? err : this.code;
      this.message = err || this.message;
    }

    this.message = text[this.code] || this.message;

    if (log) _logError(err, this);
    Sentry.captureException(err, { extra: { normalized: this } });

    return this;
  }
}

/** Log Error
 *
 * Logs the original and normalized error to the console.
 *
 * @param original - The original error input
 * @param normalized - The normalized error object
 */
const _logError = (original: unknown, normalized: NormalizedException) => {
  console.error(
    "[Original Error]:\n",
    original,
    "\n\n[Normalized Error]:\n",
    normalized,
  );
};
